/**
 * ═══════════════════════════════════════════════════════════════
 *  Carpet Room Viewer — assets/carpet-room-viewer.js
 *  MODAL POPUP VERSION
 *  - Opens as a fullscreen modal overlay (not inline panel)
 *  - Three.js 3D with working OrbitControls (drag/zoom/rotate)
 *  - Working zoom-in, zoom-out, reset, auto-rotate buttons
 *  - Shopify variant change → live texture update
 *  - 2D canvas fallback for low-end devices
 * ═══════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  /* ── CDN URLs ────────────────────────────────────────────── */
  const THREE_CDN  = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
  const ORBIT_CDN  = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.min.js';

  /* ── State ───────────────────────────────────────────────── */
  const state = {
    isOpen:        false,
    initialized:   false,
    use3D:         true,
    autoRotate:    false,
    currentRoom:   'living',
    currentImage:  window.__CRV_INITIAL_IMAGE__ || '',
    three:         null,
    canvas2d:      null,
  };

  /* ── DOM refs ────────────────────────────────────────────── */
  let dom = {};

  /* ═══════════════════════════════════════════════════════════
     BOOT
  ═══════════════════════════════════════════════════════════ */
  document.addEventListener('DOMContentLoaded', boot);

  function boot() {
    const backdrop = document.getElementById('crv-modal-backdrop');
    if (backdrop && backdrop.parentNode !== document.body) {
      document.body.appendChild(backdrop);
    }

    dom = {
      trigger:        document.getElementById('crv-trigger'),
      backdrop:       document.getElementById('crv-modal-backdrop'),
      modal:          document.getElementById('crv-modal'),
      closeBtn:       document.getElementById('crv-close-btn'),
      canvasWrap:     document.getElementById('crv-canvas-wrap'),
      canvas:         document.getElementById('crv-canvas'),
      loader:         document.getElementById('crv-loader'),
      hint:           document.getElementById('crv-hint'),
      modeBadge:      document.getElementById('crv-mode-badge'),
      autoRotateBtn:  document.getElementById('crv-autorotate-btn'),
      roomBtns:       document.querySelectorAll('.crv-room-btn'),
    };

    if (!dom.trigger || !dom.canvas || !dom.backdrop) return;

    dom.trigger.addEventListener('click', openModal);
    dom.closeBtn.addEventListener('click', closeModal);
    dom.backdrop.addEventListener('click', e => {
      if (e.target === dom.backdrop) closeModal();
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && state.isOpen) closeModal();
    });

    dom.roomBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        dom.roomBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        state.currentRoom = btn.dataset.room;
        if (state.initialized) updateRoom(state.currentRoom);
      });
    });

    document.querySelectorAll('[data-crv-action]').forEach(btn => {
      btn.addEventListener('click', handleControlAction);
    });

    listenForVariantChanges();

    /* ─────────────────────────────────────────────────────────
       EXPOSE __CRV_SET_CARPET__ globally so the variant-sync
       script (in the Liquid file) can call it directly.
       This is the key fix — previously this was never defined.
    ───────────────────────────────────────────────────────── */
    window.__CRV_SET_CARPET__ = function (imageUrl) {
      updateCarpetTexture(imageUrl);
    };
  }

  /* ═══════════════════════════════════════════════════════════
     MODAL OPEN / CLOSE
  ═══════════════════════════════════════════════════════════ */
  function openModal() {
    state.isOpen = true;
    dom.trigger.setAttribute('aria-expanded', 'true');
    dom.backdrop.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      dom.backdrop.classList.add('is-open');
    });

    if (!state.initialized) {
      setTimeout(initViewer, 80);
    }
  }

  function closeModal() {
    state.isOpen = false;
    dom.trigger.setAttribute('aria-expanded', 'false');
    dom.backdrop.setAttribute('aria-hidden', 'true');
    dom.backdrop.classList.remove('is-open');
    document.body.style.overflow = '';

    if (state.autoRotate && state.three) {
      state.three.controls.autoRotate = false;
      state.autoRotate = false;
      dom.autoRotateBtn && dom.autoRotateBtn.classList.remove('active');
    }
  }

  /* ═══════════════════════════════════════════════════════════
     VIEWER INIT
  ═══════════════════════════════════════════════════════════ */
  function initViewer() {
    showLoader(true);
    detectPerformance()
      .then(can3D => {
        state.use3D = can3D;
        dom.modeBadge.textContent = can3D ? '3D' : '2D';
        return can3D ? init3DViewer() : init2DViewer();
      })
      .then(() => {
        state.initialized = true;
        showLoader(false);
      })
      .catch(err => {
        console.warn('[CRV] 3D init failed, falling back to 2D:', err);
        state.use3D = false;
        dom.modeBadge.textContent = '2D';
        return init2DViewer();
      })
      .then(() => {
        state.initialized = true;
        showLoader(false);
      });
  }

  function detectPerformance() {
    return new Promise(resolve => {
      try {
        const c  = document.createElement('canvas');
        const gl = c.getContext('webgl') || c.getContext('experimental-webgl');
        if (!gl) return resolve(false);
        const ri = gl.getExtension('WEBGL_debug_renderer_info');
        if (ri) {
          const gpu = gl.getParameter(ri.UNMASKED_RENDERER_WEBGL).toLowerCase();
          if (gpu.includes('swiftshader') || gpu.includes('llvmpipe')) return resolve(false);
        }
        resolve(true);
      } catch {
        resolve(false);
      }
    });
  }

  /* ═══════════════════════════════════════════════════════════
     3D VIEWER — Three.js
  ═══════════════════════════════════════════════════════════ */
  function init3DViewer() {
    return loadScript(THREE_CDN)
      .then(() => loadScript(ORBIT_CDN))
      .then(setupThreeScene);
  }

  function setupThreeScene() {
    return new Promise((resolve, reject) => {
      try {
        const THREE = window.THREE;
        if (!THREE) return reject(new Error('THREE not loaded'));

        const wrap   = dom.canvasWrap;
        const canvas = dom.canvas;
        const W      = wrap.clientWidth  || 800;
        const H      = wrap.clientHeight || 450;

        const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
        renderer.setSize(W, H);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type    = THREE.PCFSoftShadowMap;
        renderer.outputEncoding    = THREE.sRGBEncoding;
        renderer.toneMapping       = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.15;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x110f0d);
        scene.fog = new THREE.Fog(0x110f0d, 14, 24);

        const camera = new THREE.PerspectiveCamera(48, W / H, 0.1, 100);
        camera.position.set(0, 3.2, 6.8);

        const controls = new THREE.OrbitControls(camera, renderer.domElement);
        controls.target.set(0, -0.5, -1);
        controls.enableDamping    = true;
        controls.dampingFactor    = 0.055;
        controls.minPolarAngle    = 0.25;
        controls.maxPolarAngle    = Math.PI / 2.05;
        controls.minDistance      = 2.5;
        controls.maxDistance      = 11;
        controls.maxAzimuthAngle  =  Math.PI / 3;
        controls.minAzimuthAngle  = -Math.PI / 3;
        controls.autoRotate       = false;
        controls.autoRotateSpeed  = 0.6;
        controls.enablePan        = false;
        controls.zoomSpeed        = 0.9;
        controls.update();

        scene.add(new THREE.AmbientLight(0xfff5e6, 0.45));

        const dirLight = new THREE.DirectionalLight(0xfff8f0, 2.0);
        dirLight.position.set(4, 9, 5);
        dirLight.castShadow = true;
        dirLight.shadow.mapSize.set(2048, 2048);
        dirLight.shadow.camera.near   = 0.5;
        dirLight.shadow.camera.far    = 32;
        dirLight.shadow.camera.left   = -7;
        dirLight.shadow.camera.right  = 7;
        dirLight.shadow.camera.top    = 7;
        dirLight.shadow.camera.bottom = -7;
        dirLight.shadow.bias = -0.001;
        scene.add(dirLight);

        const fill = new THREE.PointLight(0xffd080, 1.4, 14);
        fill.position.set(-5, 3.5, -1);
        scene.add(fill);

        const rim = new THREE.DirectionalLight(0x99bbff, 0.25);
        rim.position.set(6, 2, -5);
        scene.add(rim);

        const bounce = new THREE.PointLight(0xffcc88, 0.4, 8);
        bounce.position.set(0, -1.5, 0);
        scene.add(bounce);

        buildRoom(THREE, scene, 'living');
        const { carpetMaterial } = buildCarpet(THREE, scene);

        // Build initial furniture group (living room)
        let furnitureGroup = buildLivingFurniture(THREE);
        scene.add(furnitureGroup);

        // ── Load initial texture ──────────────────────────────
        // Use __CRV_CURRENT_IMAGE__ first (set by variant-sync if
        // user already changed variant before opening modal)
        const initImg = window.__CRV_CURRENT_IMAGE__ || state.currentImage;
        loadCarpetTexture3D(THREE, carpetMaterial, initImg);

        const ro = new ResizeObserver(() => {
          const nW = wrap.clientWidth;
          const nH = wrap.clientHeight;
          if (!nW || !nH) return;
          camera.aspect = nW / nH;
          camera.updateProjectionMatrix();
          renderer.setSize(nW, nH);
        });
        ro.observe(wrap);

        let animId;
        function animate() {
          animId = requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        }
        animate();

        state.three = { THREE, renderer, scene, camera, controls, carpetMaterial, ro, animId, furnitureGroup };

        resolve();
      } catch (err) {
        reject(err);
      }
    });
  }

  /* ── Room Geometry ───────────────────────────────────────── */

  // Room config per type
  const roomConfigs = {
    living: {
      wallColor:  0x221e18,
      floorColor: 0x2e2318,
      skirtColor: 0x150f08,
      ambientColor: 0xfff5e6, ambientIntensity: 0.45,
      fillColor: 0xffd080, fillIntensity: 1.4,
    },
    bedroom: {
      wallColor:  0x1a1820,   // cool grey-blue walls
      floorColor: 0x1e1a2a,
      skirtColor: 0x100e16,
      ambientColor: 0xe8e0ff, ambientIntensity: 0.35,
      fillColor: 0xb0a0ff, fillIntensity: 0.9,
    },
    dining: {
      wallColor:  0x1a1e10,   // warm olive-green walls
      floorColor: 0x221a0e,
      skirtColor: 0x100e08,
      ambientColor: 0xfff0d0, ambientIntensity: 0.5,
      fillColor: 0xffcc44, fillIntensity: 1.6,
    },
  };

  function buildRoom(THREE, scene, roomKey) {
    const cfg = roomConfigs[roomKey] || roomConfigs.living;
    const wallMat  = new THREE.MeshLambertMaterial({ color: cfg.wallColor, side: THREE.BackSide });
    const floorMat = new THREE.MeshLambertMaterial({ color: cfg.floorColor });

    const room = new THREE.Mesh(new THREE.BoxGeometry(13, 8, 11), wallMat);
    room.receiveShadow = true;
    room.position.y = 1.5;
    scene.add(room);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(13, 11), floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -2.0;
    floor.receiveShadow = true;
    scene.add(floor);

    const skirtMat = new THREE.MeshLambertMaterial({ color: cfg.skirtColor });
    const skirt = new THREE.Mesh(new THREE.BoxGeometry(13, 0.12, 0.05), skirtMat);
    skirt.position.set(0, -1.95, -5.48);
    scene.add(skirt);

    // Store refs for room switching
    scene.__crvRoom = { room, floor, skirt, wallMat, floorMat, skirtMat };
  }

  /* ─────────────────────────────────────────────────────────
     LIVING ROOM FURNITURE
     Sofa + coffee table + floor lamp
  ───────────────────────────────────────────────────────── */
  function buildLivingFurniture(THREE) {
    const g = new THREE.Group();

    const fabricMat = new THREE.MeshLambertMaterial({ color: 0x3e3428 });
    const darkFab   = new THREE.MeshLambertMaterial({ color: 0x2e2820 });
    const woodMat   = new THREE.MeshLambertMaterial({ color: 0x1e1408 });
    const cushMat   = new THREE.MeshLambertMaterial({ color: 0x4a3e2e });

    // Sofa seat
    const seat = new THREE.Mesh(new THREE.BoxGeometry(3.8, 0.52, 1.2), fabricMat);
    seat.position.set(0, -1.74, -3.9); seat.castShadow = true; g.add(seat);

    // Sofa back
    const back = new THREE.Mesh(new THREE.BoxGeometry(3.8, 1.0, 0.28), fabricMat);
    back.position.set(0, -1.24, -4.35); back.castShadow = true; g.add(back);

    // Arms
    [-1.75, 1.75].forEach(x => {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.62, 1.2), darkFab);
      arm.position.set(x, -1.69, -3.9); arm.castShadow = true; g.add(arm);
    });

    // Cushions
    [-1.1, 0, 1.1].forEach(x => {
      const c = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.38, 0.9), cushMat);
      c.position.set(x, -1.46, -4.0); c.castShadow = true; g.add(c);
    });

    // Sofa legs
    [[-1.6,-3.5],[1.6,-3.5],[-1.6,-4.3],[1.6,-4.3]].forEach(([x,z]) => {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.26, 0.08), woodMat);
      leg.position.set(x, -2.13, z); g.add(leg);
    });

    // Coffee table top
    const tabTop = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.07, 0.85), woodMat);
    tabTop.position.set(0, -1.72, -2.0); tabTop.castShadow = true; tabTop.receiveShadow = true; g.add(tabTop);

    // Table legs
    [[-0.6,-1.65],[0.6,-1.65],[-0.6,-2.35],[0.6,-2.35]].forEach(([x,z]) => {
      const tl = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.46, 0.06), woodMat);
      tl.position.set(x, -1.97, z); g.add(tl);
    });

    // Floor lamp pole
    const poleMat  = new THREE.MeshLambertMaterial({ color: 0x4a3a20 });
    const shadeMat = new THREE.MeshLambertMaterial({ color: 0x8b7040, emissive: 0x3a2a10, emissiveIntensity: 0.4 });
    const pole  = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 2.2, 8), poleMat);
    const shade = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.45, 16), shadeMat);
    pole.position.set(2.4, -0.9, -4.0);
    shade.position.set(2.4, 0.18, -4.0);
    g.add(pole); g.add(shade);

    const lampGlow = new THREE.PointLight(0xffc060, 0.7, 4.5);
    lampGlow.position.set(2.4, -0.1, -4.0);
    g.add(lampGlow);

    return g;
  }

  /* ─────────────────────────────────────────────────────────
     BEDROOM FURNITURE
     Double bed + bedside tables + pendant lamp
  ───────────────────────────────────────────────────────── */
  function buildBedroomFurniture(THREE) {
    const g = new THREE.Group();

    const headMat  = new THREE.MeshLambertMaterial({ color: 0x2a2030 }); // dark purple headboard
    const bedMat   = new THREE.MeshLambertMaterial({ color: 0x3a3248 }); // bedframe
    const sheetMat = new THREE.MeshLambertMaterial({ color: 0xd8d0e8 }); // light sheets
    const pillMat  = new THREE.MeshLambertMaterial({ color: 0xf0ecf8 }); // pillows
    const woodMat  = new THREE.MeshLambertMaterial({ color: 0x1e1828 }); // dark wood
    const lampMat  = new THREE.MeshLambertMaterial({ color: 0xb09858, emissive: 0x3a2a08, emissiveIntensity: 0.6 });

    // Bed frame base
    const base = new THREE.Mesh(new THREE.BoxGeometry(3.2, 0.28, 4.8), bedMat);
    base.position.set(0, -2.0, -3.4); base.castShadow = true; g.add(base);

    // Mattress
    const mattress = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.32, 4.4), sheetMat);
    mattress.position.set(0, -1.73, -3.4); mattress.castShadow = true; g.add(mattress);

    // Headboard
    const headboard = new THREE.Mesh(new THREE.BoxGeometry(3.2, 1.6, 0.18), headMat);
    headboard.position.set(0, -1.1, -5.5); headboard.castShadow = true; g.add(headboard);

    // Duvet / cover (slightly raised at foot end)
    const duvet = new THREE.Mesh(new THREE.BoxGeometry(2.9, 0.22, 3.0), sheetMat);
    duvet.position.set(0, -1.52, -2.6); duvet.castShadow = true; g.add(duvet);

    // Pillows
    [-0.72, 0.72].forEach(x => {
      const p = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.22, 0.65), pillMat);
      p.position.set(x, -1.54, -4.8); p.castShadow = true; g.add(p);
    });

    // Bed legs
    [[-1.5,-1.7],[1.5,-1.7],[-1.5,-5.1],[1.5,-5.1]].forEach(([x,z]) => {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.32, 0.1), woodMat);
      leg.position.set(x, -2.21, z); g.add(leg);
    });

    // Bedside tables
    [-2.2, 2.2].forEach(x => {
      const tbl = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.6, 0.7), woodMat);
      tbl.position.set(x, -1.75, -4.8); tbl.castShadow = true; tbl.receiveShadow = true; g.add(tbl);

      // Table lamp shade (small)
      const lshade = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.24, 0.3, 12), lampMat);
      lshade.position.set(x, -1.3, -4.8); g.add(lshade);

      // Glow
      const glow = new THREE.PointLight(0xfff0b0, 0.5, 3.5);
      glow.position.set(x, -1.4, -4.8); g.add(glow);
    });

    // Ceiling pendant (moody bedroom feel)
    const pendantMat = new THREE.MeshLambertMaterial({ color: 0x888080, emissive: 0x222020, emissiveIntensity: 0.3 });
    const pendant = new THREE.Mesh(new THREE.SphereGeometry(0.22, 12, 8), pendantMat);
    pendant.position.set(0, 1.8, -3.0); g.add(pendant);

    const pendantGlow = new THREE.PointLight(0xd0c0ff, 0.8, 7);
    pendantGlow.position.set(0, 1.6, -3.0); g.add(pendantGlow);

    return g;
  }

  /* ─────────────────────────────────────────────────────────
     DINING ROOM FURNITURE
     Dining table + 4 chairs + pendant chandelier
  ───────────────────────────────────────────────────────── */
  function buildDiningFurniture(THREE) {
    const g = new THREE.Group();

    const tableMat = new THREE.MeshLambertMaterial({ color: 0x2a1e0e }); // dark walnut
    const chairMat = new THREE.MeshLambertMaterial({ color: 0x3a2a14 }); // chair frame
    const seatMat  = new THREE.MeshLambertMaterial({ color: 0x1e3020 }); // dark green upholstery
    const metalMat = new THREE.MeshLambertMaterial({ color: 0x888070 }); // chandelier

    // Dining table top
    const top = new THREE.Mesh(new THREE.BoxGeometry(3.6, 0.1, 1.8), tableMat);
    top.position.set(0, -1.5, -3.0); top.castShadow = true; top.receiveShadow = true; g.add(top);

    // Table legs (4)
    [[-1.65,-2.1],[1.65,-2.1],[-1.65,-3.9],[1.65,-3.9]].forEach(([x,z]) => {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.1, 0.1), tableMat);
      leg.position.set(x, -2.05, z); g.add(leg);
    });

    // Chairs — 2 front, 2 back
    const chairPositions = [
      { x:-1.1, z:-1.1, ry: 0       },  // front-left
      { x: 1.1, z:-1.1, ry: 0       },  // front-right
      { x:-1.1, z:-4.9, ry: Math.PI },  // back-left
      { x: 1.1, z:-4.9, ry: Math.PI },  // back-right
    ];

    chairPositions.forEach(({ x, z, ry }) => {
      const cg = new THREE.Group();
      cg.rotation.y = ry;

      // Seat pad
      const cSeat = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.1, 0.68), seatMat);
      cSeat.position.set(0, -1.76, 0); cg.add(cSeat);

      // Chair back
      const cBack = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.78, 0.08), chairMat);
      cBack.position.set(0, -1.38, -0.3); cg.add(cBack);

      // Chair legs
      [[-0.3,-0.28],[0.3,-0.28],[-0.3,0.28],[0.3,0.28]].forEach(([cx,cz]) => {
        const cl = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.56, 0.06), chairMat);
        cl.position.set(cx, -2.04, cz); cg.add(cl);
      });

      cg.position.set(x, 0, z);
      g.add(cg);
    });

    // Overhead chandelier
    const armMat = new THREE.MeshLambertMaterial({ color: 0x706050 });
    // Central disc
    const disc = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.06, 16), metalMat);
    disc.position.set(0, 1.6, -3.0); g.add(disc);

    // 4 arms
    [0, Math.PI/2, Math.PI, Math.PI*1.5].forEach(angle => {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.04, 0.04), armMat);
      arm.position.set(Math.sin(angle)*0.4, 1.55, -3.0 + Math.cos(angle)*0.4);
      arm.rotation.y = angle; g.add(arm);

      // Bulb
      const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 6), metalMat);
      bulb.position.set(Math.sin(angle)*0.75, 1.42, -3.0 + Math.cos(angle)*0.75); g.add(bulb);
    });

    // Chandelier warm glow
    const chanGlow = new THREE.PointLight(0xffdd88, 2.2, 8);
    chanGlow.position.set(0, 1.4, -3.0); g.add(chanGlow);

    // Side wall sconce (right)
    const sconceGlow = new THREE.PointLight(0xffbb44, 0.6, 5);
    sconceGlow.position.set(5, 0.5, -3.0); g.add(sconceGlow);

    return g;
  }

  /* ── Carpet positions per room ───────────────────────────── */
  // Living:  carpet under coffee table, in front of sofa
  // Bedroom: carpet at foot of bed, visible in front (bed is at z -3.4 to -5.5)
  // Dining:  carpet under dining table and chairs (table at z -3.0)
  // Carpet covers the ENTIRE floor (13 × 11) in all rooms.
  // Fringe is slightly bigger to create a border effect.
  // z offset (-2.75) centers carpet within the visible floor area.
  const carpetPositions = {
    living:  { x: 0, z: -2.75, w: 12.8, h: 10.8, fw: 13.0, fh: 11.0 },
    bedroom: { x: 0, z: -2.75, w: 12.8, h: 10.8, fw: 13.0, fh: 11.0 },
    dining:  { x: 0, z: -2.75, w: 12.8, h: 10.8, fw: 13.0, fh: 11.0 },
  };

  /* ── Carpet ──────────────────────────────────────────────── */
  function buildCarpet(THREE, scene) {
    const cp = carpetPositions.living; // start with living

    // Fringe plane (same size as carpet — no visible border needed for full-floor carpet)
    const fringeMat  = new THREE.MeshStandardMaterial({ color: 0xd4c4a0, roughness: 0.95 });
    const fringeMesh = new THREE.Mesh(new THREE.PlaneGeometry(cp.fw, cp.fh), fringeMat);
    fringeMesh.rotation.x = -Math.PI / 2;
    fringeMesh.position.set(cp.x, -1.996, cp.z);
    fringeMesh.receiveShadow = true;
    scene.add(fringeMesh);

    const carpetMaterial = new THREE.MeshStandardMaterial({
      color:     0xffffff,
      roughness: 0.88,
      metalness: 0.0,
    });
    const carpetMesh = new THREE.Mesh(new THREE.PlaneGeometry(cp.w, cp.h, 1, 1), carpetMaterial);
    carpetMesh.rotation.x = -Math.PI / 2;
    carpetMesh.position.set(cp.x, -1.99, cp.z);
    carpetMesh.receiveShadow = true;
    scene.add(carpetMesh);

    // Store refs so updateRoom can reposition
    scene.__crvCarpet = { carpetMesh, fringeMesh };

    return { carpetMesh, carpetMaterial };
  }

  /* ── Load texture (3D) ───────────────────────────────────── */
  function loadCarpetTexture3D(THREE, material, imageUrl) {
    if (!imageUrl) return;
    new THREE.TextureLoader().load(
      imageUrl,
      tex => {
        tex.encoding  = THREE.sRGBEncoding;
        // Tile the texture across the full floor (repeat 4×4 times)
        // so it looks like real carpet — not one giant stretched image
        tex.wrapS     = THREE.RepeatWrapping;
        tex.wrapT     = THREE.RepeatWrapping;
        tex.repeat.set(4, 4);
        material.map   = tex;
        material.color.set(0xffffff);
        material.needsUpdate = true;
        flashCanvasUpdate();
      },
      undefined,
      () => {
        material.color.set(0x8b7355);
        material.needsUpdate = true;
      }
    );
  }

  /* ── Room Presets ────────────────────────────────────────── */
  const roomPresets = {
    living:  { camPos: [0, 3.2, 6.8],    target: [0, -0.5, -1], bgColor: 0x110f0d, fogColor: 0x110f0d },
    bedroom: { camPos: [-0.8, 3.6, 6.2], target: [0, -0.5, -1], bgColor: 0x0f0d12, fogColor: 0x0f0d12 },
    dining:  { camPos: [0, 4.0, 6.2],    target: [0, -0.5, -1], bgColor: 0x0c100a, fogColor: 0x0c100a },
  };

  function updateRoom(key) {
    if (state.use3D && state.three) {
      const { THREE, camera, controls, scene } = state.three;
      const p   = roomPresets[key] || roomPresets.living;
      const cfg = roomConfigs[key] || roomConfigs.living;

      // Camera + atmosphere
      camera.position.set(...p.camPos);
      controls.target.set(...p.target);
      scene.background.set(p.bgColor);
      scene.fog.color.set(p.fogColor);
      controls.update();

      // Wall / floor colors
      if (scene.__crvRoom) {
        scene.__crvRoom.wallMat.color.setHex(cfg.wallColor);
        scene.__crvRoom.floorMat.color.setHex(cfg.floorColor);
        scene.__crvRoom.skirtMat.color.setHex(cfg.skirtColor);
      }

      // Swap ambient + fill light colors
      scene.traverse(obj => {
        if (obj.isAmbientLight) {
          obj.color.setHex(cfg.ambientColor);
          obj.intensity = cfg.ambientIntensity;
        }
      });

      // Swap furniture group
      if (state.three.furnitureGroup) {
        scene.remove(state.three.furnitureGroup);
        // Dispose geometries/materials to free GPU memory
        state.three.furnitureGroup.traverse(obj => {
          if (obj.isMesh) {
            obj.geometry && obj.geometry.dispose();
            if (obj.material) {
              Array.isArray(obj.material)
                ? obj.material.forEach(m => m.dispose())
                : obj.material.dispose();
            }
          }
        });
      }

      const builders = {
        living:  buildLivingFurniture,
        bedroom: buildBedroomFurniture,
        dining:  buildDiningFurniture,
      };
      const newGroup = (builders[key] || builders.living)(THREE);
      scene.add(newGroup);
      state.three.furnitureGroup = newGroup;

      // ── Carpet reposition per room ──────────────────────────
      // Each room has different carpet position & size defined in carpetPositions.
      // We rebuild carpet geometry so it fills the correct area in each room.
      if (scene.__crvCarpet) {
        const cp = carpetPositions[key] || carpetPositions.living;
        const { carpetMesh, fringeMesh } = scene.__crvCarpet;

        // Rebuild geometry with correct size for this room
        carpetMesh.geometry.dispose();
        fringeMesh.geometry.dispose();
        carpetMesh.geometry = new THREE.PlaneGeometry(cp.w, cp.h, 1, 1);
        fringeMesh.geometry = new THREE.PlaneGeometry(cp.fw, cp.fh);

        // Reposition to correct location in the room
        carpetMesh.position.set(cp.x, -1.99, cp.z);
        fringeMesh.position.set(cp.x, -1.995, cp.z);
      }

    } else {
      render2D();
    }
  }

  /* ═══════════════════════════════════════════════════════════
     CAMERA CONTROLS (buttons)
  ═══════════════════════════════════════════════════════════ */
  function handleControlAction(e) {
    const action = e.currentTarget.dataset.crvAction;

    if (state.use3D && state.three) {
      const { camera, controls } = state.three;

      if (action === 'zoom-in') {
        const dir = new window.THREE.Vector3()
          .subVectors(controls.target, camera.position)
          .normalize();
        const dist = camera.position.distanceTo(controls.target);
        const step = Math.max(0.4, dist * 0.18);
        camera.position.addScaledVector(dir, step);
        controls.update();

      } else if (action === 'zoom-out') {
        const dir = new window.THREE.Vector3()
          .subVectors(camera.position, controls.target)
          .normalize();
        const dist = camera.position.distanceTo(controls.target);
        const step = Math.max(0.4, dist * 0.18);
        camera.position.addScaledVector(dir, step);
        controls.update();

      } else if (action === 'reset') {
        const preset = roomPresets[state.currentRoom] || roomPresets.living;
        camera.position.set(...preset.camPos);
        controls.target.set(...preset.target);
        controls.update();

      } else if (action === 'autorotate') {
        state.autoRotate = !state.autoRotate;
        controls.autoRotate = state.autoRotate;
        e.currentTarget.style.borderColor = state.autoRotate ? 'var(--crv-accent)' : '';
        e.currentTarget.style.color       = state.autoRotate ? 'var(--crv-accent)' : '';
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════
     2D FALLBACK
  ═══════════════════════════════════════════════════════════ */
  function init2DViewer() {
    return new Promise(resolve => {
      const canvas = dom.canvas;
      const wrap   = dom.canvasWrap;
      const dpr    = Math.min(window.devicePixelRatio, 2);

      function resize() {
        canvas.width  = wrap.clientWidth  * dpr;
        canvas.height = wrap.clientHeight * dpr;
        canvas.style.width  = wrap.clientWidth  + 'px';
        canvas.style.height = wrap.clientHeight + 'px';
      }
      resize();

      const ctx = canvas.getContext('2d');
      state.canvas2d = { ctx, carpetImg: null, tiltY: 0, tiltX: 0 };

      const ro = new ResizeObserver(() => { resize(); render2D(); });
      ro.observe(wrap);
      state.canvas2d.ro = ro;

      let drag = null;
      canvas.addEventListener('mousedown', e => { drag = { x: e.clientX, y: e.clientY }; });
      canvas.addEventListener('mousemove', e => {
        if (!drag) return;
        state.canvas2d.tiltY = Math.max(-25, Math.min(25, (e.clientY - drag.y) * 0.28));
        state.canvas2d.tiltX = Math.max(-15, Math.min(15, (e.clientX - drag.x) * 0.12));
        render2D();
      });
      canvas.addEventListener('mouseup',    () => { drag = null; });
      canvas.addEventListener('mouseleave', () => { drag = null; });

      canvas.addEventListener('touchstart', e => {
        drag = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }, { passive: true });
      canvas.addEventListener('touchmove', e => {
        if (!drag) return;
        state.canvas2d.tiltY = Math.max(-25, Math.min(25, (e.touches[0].clientY - drag.y) * 0.28));
        state.canvas2d.tiltX = Math.max(-15, Math.min(15, (e.touches[0].clientX - drag.x) * 0.12));
        render2D();
      }, { passive: true });
      canvas.addEventListener('touchend', () => { drag = null; });

      // Use __CRV_CURRENT_IMAGE__ if already set by variant-sync
      const initImg = window.__CRV_CURRENT_IMAGE__ || state.currentImage;
      if (initImg) {
        loadCarpetTexture2D(initImg).then(() => { render2D(); resolve(); });
      } else {
        render2D();
        resolve();
      }
    });
  }

  function loadCarpetTexture2D(url) {
    return new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { state.canvas2d.carpetImg = img; resolve(img); };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  const room2DPalettes = {
    living:  { sky: ['#110f0d','#1e1a14'], floor: ['#2a1e12','#160e08'], wall: '#1e1810', wall2: '#18120c', carpet: '#3a2e20', light: 'rgba(255,220,140,0.20)' },
    bedroom: { sky: ['#0f0d14','#1a1528'], floor: ['#1a1620','#0e0c18'], wall: '#181228', wall2: '#100e20', carpet: '#2e2848', light: 'rgba(160,140,255,0.18)' },
    dining:  { sky: ['#0c100a','#161e0e'], floor: ['#1a1e0e','#0e1208'], wall: '#12180c', wall2: '#0e1408', carpet: '#1e2a12', light: 'rgba(255,220,80,0.22)'  },
  };

  function render2D() {
    if (!state.canvas2d) return;
    const { ctx, tiltY = 0, tiltX = 0 } = state.canvas2d;
    const W = dom.canvas.width;
    const H = dom.canvas.height;
    const pal = room2DPalettes[state.currentRoom] || room2DPalettes.living;

    ctx.clearRect(0, 0, W, H);

    const vp = { x: W / 2 + tiltX * W * 0.01, y: H * 0.38 + tiltY };
    const ww = W * 0.24;

    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, pal.sky[0]);
    bg.addColorStop(1, pal.sky[1]);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = pal.wall;
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(W, 0);
    ctx.lineTo(vp.x + ww, vp.y); ctx.lineTo(vp.x - ww, vp.y);
    ctx.closePath(); ctx.fill();

    ctx.fillStyle = pal.wall2;
    ctx.beginPath();
    ctx.moveTo(vp.x - ww, vp.y); ctx.lineTo(vp.x + ww, vp.y);
    ctx.lineTo(vp.x + ww, vp.y + H * 0.36); ctx.lineTo(vp.x - ww, vp.y + H * 0.36);
    ctx.closePath(); ctx.fill();

    ctx.fillStyle = pal.wall;
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(vp.x - ww, vp.y);
    ctx.lineTo(vp.x - ww, vp.y + H * 0.36); ctx.lineTo(0, H);
    ctx.closePath(); ctx.fill();

    ctx.fillStyle = pal.wall;
    ctx.beginPath();
    ctx.moveTo(W, 0); ctx.lineTo(vp.x + ww, vp.y);
    ctx.lineTo(vp.x + ww, vp.y + H * 0.36); ctx.lineTo(W, H);
    ctx.closePath(); ctx.fill();

    const flGrad = ctx.createLinearGradient(0, vp.y + H * 0.36, 0, H);
    flGrad.addColorStop(0, pal.floor[0]);
    flGrad.addColorStop(1, pal.floor[1]);
    ctx.fillStyle = flGrad;
    ctx.beginPath();
    ctx.moveTo(0, H); ctx.lineTo(W, H);
    ctx.lineTo(vp.x + ww, vp.y + H * 0.36); ctx.lineTo(vp.x - ww, vp.y + H * 0.36);
    ctx.closePath(); ctx.fill();

    const bloom = ctx.createRadialGradient(vp.x, 0, 0, vp.x, 0, W * 0.4);
    bloom.addColorStop(0, pal.light);
    bloom.addColorStop(1, 'transparent');
    ctx.fillStyle = bloom;
    ctx.fillRect(0, 0, W, H * 0.55);

    drawCarpet2D(ctx, W, H, vp, pal);
    drawFurniture2D(ctx, W, H, vp);

    const vig = ctx.createRadialGradient(W/2, H/2, H*0.25, W/2, H/2, H*0.9);
    vig.addColorStop(0, 'transparent');
    vig.addColorStop(1, 'rgba(0,0,0,0.6)');
    ctx.fillStyle = vig;
    ctx.fillRect(0, 0, W, H);
  }

  function drawCarpet2D(ctx, W, H, vp, pal) {
    const cx  = vp.x;
    // Full floor corners in 2D perspective:
    // Top edge = horizon line where floor meets back wall
    // Bottom edge = bottom of canvas (camera near-clip)
    const floorTopY = vp.y + H * 0.36;    // same as floor gradient start
    const floorBotY = H;                   // bottom of canvas

    const TL = { x: 0,  y: floorTopY };   // left horizon corner
    const TR = { x: W,  y: floorTopY };   // right horizon corner
    const BR = { x: W,  y: floorBotY  };  // bottom-right
    const BL = { x: 0,  y: floorBotY  };  // bottom-left

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(TL.x, TL.y); ctx.lineTo(TR.x, TR.y);
    ctx.lineTo(BR.x, BR.y); ctx.lineTo(BL.x, BL.y);
    ctx.closePath(); ctx.clip();

    if (state.canvas2d.carpetImg) {
      drawImagePerspective(ctx, state.canvas2d.carpetImg, TL, TR, BR, BL);
    } else {
      ctx.fillStyle = pal.carpet;
      ctx.fillRect(0, 0, W, H);
    }

    // Subtle sheen overlay
    const sheen = ctx.createLinearGradient(TL.x, TL.y, BL.x, BL.y);
    sheen.addColorStop(0, 'rgba(255,255,255,0.07)');
    sheen.addColorStop(0.5, 'rgba(255,255,255,0.0)');
    sheen.addColorStop(1, 'rgba(0,0,0,0.12)');
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, W, H);

    ctx.restore();
  }

  function drawImagePerspective(ctx, img, TL, TR, BR, BL) {
    const iW = img.naturalWidth, iH = img.naturalHeight;
    ctx.save();
    ctx.beginPath(); ctx.moveTo(TL.x, TL.y); ctx.lineTo(TR.x, TR.y); ctx.lineTo(BL.x, BL.y); ctx.closePath(); ctx.clip();
    applyAffine(ctx, img, {x:0,y:0},{x:iW,y:0},{x:0,y:iH}, TL, TR, BL);
    ctx.restore();
    ctx.save();
    ctx.beginPath(); ctx.moveTo(TR.x, TR.y); ctx.lineTo(BR.x, BR.y); ctx.lineTo(BL.x, BL.y); ctx.closePath(); ctx.clip();
    applyAffine(ctx, img, {x:iW,y:0},{x:iW,y:iH},{x:0,y:iH}, TR, BR, BL);
    ctx.restore();
  }

  function applyAffine(ctx, img, s0, s1, s2, d0, d1, d2) {
    const D = (s1.x-s0.x)*(s2.y-s0.y) - (s2.x-s0.x)*(s1.y-s0.y);
    if (Math.abs(D) < 1e-6) return;
    const a = ((d1.x-d0.x)*(s2.y-s0.y)-(d2.x-d0.x)*(s1.y-s0.y))/D;
    const b = ((d2.x-d0.x)*(s1.x-s0.x)-(d1.x-d0.x)*(s2.x-s0.x))/D;
    const c = d0.x - a*s0.x - b*s0.y;
    const d = ((d1.y-d0.y)*(s2.y-s0.y)-(d2.y-d0.y)*(s1.y-s0.y))/D;
    const e = ((d2.y-d0.y)*(s1.x-s0.x)-(d1.y-d0.y)*(s2.x-s0.x))/D;
    const f = d0.y - d*s0.x - e*s0.y;
    ctx.transform(a,d,b,e,c,f);
    ctx.drawImage(img, 0, 0);
  }

  function drawFurniture2D(ctx, W, H, vp) {
    const room = state.currentRoom || 'living';
    if (room === 'bedroom') {
      drawBedroom2D(ctx, W, H, vp);
    } else if (room === 'dining') {
      drawDining2D(ctx, W, H, vp);
    } else {
      drawLiving2D(ctx, W, H, vp);
    }
  }

  function drawLiving2D(ctx, W, H, vp) {
    const baseY = vp.y + H * 0.33;

    // Sofa base
    ctx.fillStyle = 'rgba(38,28,14,0.92)';
    roundRect(ctx, W/2 - W*0.19, baseY - H*0.09, W*0.38, H*0.10, 5); ctx.fill();
    // Sofa back
    ctx.fillStyle = 'rgba(48,36,18,0.95)';
    roundRect(ctx, W/2 - W*0.19, baseY - H*0.20, W*0.38, H*0.12, 4); ctx.fill();
    // Arms
    ctx.fillStyle = 'rgba(34,24,10,0.95)';
    roundRect(ctx, W/2 - W*0.22, baseY - H*0.13, W*0.03, H*0.10, 3); ctx.fill();
    roundRect(ctx, W/2 + W*0.19, baseY - H*0.13, W*0.03, H*0.10, 3); ctx.fill();
    // Cushions
    ctx.fillStyle = 'rgba(70,55,30,0.75)';
    [-0.08, 0, 0.08].forEach(off => {
      roundRect(ctx, W/2 + off*W - W*0.052, baseY - H*0.17, W*0.098, H*0.07, 6);
      ctx.fill();
    });
    // Coffee table
    ctx.fillStyle = 'rgba(20,12,5,0.85)';
    roundRect(ctx, W/2 - W*0.09, baseY + H*0.025, W*0.18, H*0.04, 3);
    ctx.fill();
    // Floor lamp
    ctx.strokeStyle = 'rgba(90,70,30,0.7)';
    ctx.lineWidth = 2;
    const lampX = vp.x + W * 0.27;
    ctx.beginPath();
    ctx.moveTo(lampX, baseY + H*0.06);
    ctx.lineTo(lampX, baseY - H*0.32);
    ctx.stroke();
    ctx.fillStyle = 'rgba(160,120,50,0.3)';
    ctx.beginPath();
    ctx.ellipse(lampX, baseY - H*0.34, W*0.045, H*0.045, 0, 0, Math.PI*2);
    ctx.fill();
    const glow = ctx.createRadialGradient(lampX, baseY - H*0.3, 0, lampX, baseY - H*0.3, W*0.13);
    glow.addColorStop(0, 'rgba(255,200,80,0.22)');
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow;
    ctx.fillRect(lampX - W*0.13, baseY - H*0.45, W*0.26, H*0.28);
  }

  function drawBedroom2D(ctx, W, H, vp) {
    const baseY = vp.y + H * 0.28;

    // Headboard
    ctx.fillStyle = 'rgba(28,20,38,0.97)';
    roundRect(ctx, W/2 - W*0.22, baseY - H*0.28, W*0.44, H*0.20, 6); ctx.fill();

    // Bed base
    ctx.fillStyle = 'rgba(38,30,52,0.95)';
    roundRect(ctx, W/2 - W*0.22, baseY - H*0.10, W*0.44, H*0.14, 4); ctx.fill();

    // Sheets/mattress (lighter)
    ctx.fillStyle = 'rgba(195,185,220,0.85)';
    roundRect(ctx, W/2 - W*0.20, baseY - H*0.09, W*0.40, H*0.10, 4); ctx.fill();

    // Duvet (slightly different tone)
    ctx.fillStyle = 'rgba(180,172,208,0.70)';
    roundRect(ctx, W/2 - W*0.19, baseY - H*0.07, W*0.38, H*0.06, 3); ctx.fill();

    // Two pillows
    ctx.fillStyle = 'rgba(230,225,245,0.90)';
    roundRect(ctx, W/2 - W*0.17, baseY - H*0.175, W*0.13, H*0.065, 5); ctx.fill();
    roundRect(ctx, W/2 + W*0.04, baseY - H*0.175, W*0.13, H*0.065, 5); ctx.fill();

    // Bedside tables
    [-0.26, 0.23].forEach(off => {
      const bx = W/2 + off*W;
      ctx.fillStyle = 'rgba(18,14,28,0.90)';
      roundRect(ctx, bx - W*0.04, baseY - H*0.11, W*0.08, H*0.09, 3); ctx.fill();
      // Lamp shade
      ctx.fillStyle = 'rgba(180,158,80,0.60)';
      ctx.beginPath();
      ctx.ellipse(bx, baseY - H*0.13, W*0.025, H*0.022, 0, 0, Math.PI*2);
      ctx.fill();
      // Glow
      const g2 = ctx.createRadialGradient(bx, baseY - H*0.12, 0, bx, baseY - H*0.12, W*0.09);
      g2.addColorStop(0, 'rgba(255,240,160,0.28)');
      g2.addColorStop(1, 'transparent');
      ctx.fillStyle = g2;
      ctx.fillRect(bx - W*0.09, baseY - H*0.22, W*0.18, H*0.14);
    });

    // Pendant glow from top center
    const pendG = ctx.createRadialGradient(W/2, vp.y, 0, W/2, vp.y, W*0.28);
    pendG.addColorStop(0, 'rgba(200,180,255,0.25)');
    pendG.addColorStop(1, 'transparent');
    ctx.fillStyle = pendG;
    ctx.fillRect(W/2 - W*0.28, vp.y - H*0.05, W*0.56, H*0.35);
  }

  function drawDining2D(ctx, W, H, vp) {
    const baseY = vp.y + H * 0.36;

    // Dining table top (wide, shorter depth due to perspective)
    ctx.fillStyle = 'rgba(25,16,6,0.96)';
    roundRect(ctx, W/2 - W*0.22, baseY - H*0.06, W*0.44, H*0.07, 3); ctx.fill();

    // Table edge highlight
    ctx.fillStyle = 'rgba(60,40,15,0.55)';
    roundRect(ctx, W/2 - W*0.22, baseY - H*0.065, W*0.44, H*0.015, 2); ctx.fill();

    // 4 chairs — 2 in front of table, 2 behind (partially hidden)
    // Back chairs (drawn first, partially behind table)
    ctx.fillStyle = 'rgba(22,32,14,0.70)';
    roundRect(ctx, W/2 - W*0.16, baseY - H*0.24, W*0.10, H*0.19, 4); ctx.fill();
    roundRect(ctx, W/2 + W*0.06, baseY - H*0.24, W*0.10, H*0.19, 4); ctx.fill();
    // Back chair backs (above table)
    ctx.fillStyle = 'rgba(32,44,20,0.75)';
    roundRect(ctx, W/2 - W*0.155, baseY - H*0.36, W*0.09, H*0.13, 4); ctx.fill();
    roundRect(ctx, W/2 + W*0.065, baseY - H*0.36, W*0.09, H*0.13, 4); ctx.fill();

    // Front chairs (in front of table)
    ctx.fillStyle = 'rgba(28,40,18,0.92)';
    roundRect(ctx, W/2 - W*0.16, baseY + H*0.025, W*0.10, H*0.085, 4); ctx.fill();
    roundRect(ctx, W/2 + W*0.06, baseY + H*0.025, W*0.10, H*0.085, 4); ctx.fill();
    // Front chair backs
    ctx.fillStyle = 'rgba(36,50,22,0.90)';
    roundRect(ctx, W/2 - W*0.155, baseY - H*0.065, W*0.09, H*0.095, 4); ctx.fill();
    roundRect(ctx, W/2 + W*0.065, baseY - H*0.065, W*0.09, H*0.095, 4); ctx.fill();

    // Chandelier / pendant above table
    // Chain
    ctx.strokeStyle = 'rgba(120,100,60,0.6)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(W/2, vp.y + H*0.02);
    ctx.lineTo(W/2, baseY - H*0.22);
    ctx.stroke();

    // Chandelier body
    ctx.fillStyle = 'rgba(140,120,70,0.80)';
    ctx.beginPath();
    ctx.ellipse(W/2, baseY - H*0.23, W*0.055, H*0.022, 0, 0, Math.PI*2);
    ctx.fill();

    // Arms of chandelier
    ctx.strokeStyle = 'rgba(130,110,60,0.65)';
    ctx.lineWidth = 1.5;
    [-0.065, 0.065].forEach(off => {
      ctx.beginPath();
      ctx.moveTo(W/2, baseY - H*0.235);
      ctx.lineTo(W/2 + off*W, baseY - H*0.20);
      ctx.stroke();
      // Bulbs
      ctx.fillStyle = 'rgba(255,220,100,0.55)';
      ctx.beginPath();
      ctx.arc(W/2 + off*W, baseY - H*0.20, W*0.012, 0, Math.PI*2);
      ctx.fill();
    });

    // Chandelier warm glow
    const chanG = ctx.createRadialGradient(W/2, baseY - H*0.20, 0, W/2, baseY - H*0.20, W*0.30);
    chanG.addColorStop(0, 'rgba(255,210,80,0.30)');
    chanG.addColorStop(1, 'transparent');
    ctx.fillStyle = chanG;
    ctx.fillRect(W/2 - W*0.30, baseY - H*0.45, W*0.60, H*0.38);
  }

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x+r, y);
    ctx.lineTo(x+w-r, y); ctx.quadraticCurveTo(x+w, y, x+w, y+r);
    ctx.lineTo(x+w, y+h-r); ctx.quadraticCurveTo(x+w, y+h, x+w-r, y+h);
    ctx.lineTo(x+r, y+h); ctx.quadraticCurveTo(x, y+h, x, y+h-r);
    ctx.lineTo(x, y+r); ctx.quadraticCurveTo(x, y, x+r, y);
    ctx.closePath();
  }

  /* ═══════════════════════════════════════════════════════════
     SHOPIFY VARIANT CHANGE LISTENER
  ═══════════════════════════════════════════════════════════ */
  function listenForVariantChanges() {
    document.addEventListener('variant:changed', onVariantChanged);
    document.addEventListener('variantChange',   onVariantChanged);

    document.addEventListener('change', e => {
      if (e.target.matches('variant-radios input, variant-selects select, [data-variant-id]')) {
        const form = e.target.closest('form');
        const hiddenId = form && form.querySelector('input[name="id"]');
        if (hiddenId) fetchVariantImage(hiddenId.value);
      }
    });

    const variantInput = document.querySelector('input[name="id"]');
    if (variantInput) {
      const mo = new MutationObserver(() => fetchVariantImage(variantInput.value));
      mo.observe(variantInput, { attributes: true, attributeFilter: ['value'] });

      document.querySelectorAll('select[name="id"], input[type="radio"][name="id"]').forEach(el => {
        el.addEventListener('change', () => fetchVariantImage(el.value));
      });
    }

    // __CRV_VARIANTS__ map — instant lookup on any change event
    document.addEventListener('change', e => {
      const val = e.target.value;
      if (!val || !window.__CRV_VARIANTS__) return;
      const match = window.__CRV_VARIANTS__.find(v => String(v.id) === String(val));
      if (match && match.image) updateCarpetTexture(match.image);
    });
  }

  function onVariantChanged(e) {
    const variant = e.detail?.variant || e.detail;
    if (!variant) return;
    const src = variant.featured_image?.src || variant.image?.src;
    if (src) updateCarpetTexture(ensureSize(src, 800));
  }

  function fetchVariantImage(variantId) {
    if (!variantId) return;

    if (window.__CRV_VARIANTS__) {
      const match = window.__CRV_VARIANTS__.find(v => String(v.id) === String(variantId));
      if (match && match.image) {
        updateCarpetTexture(match.image);
        return;
      }
    }

    const handle = window.location.pathname.split('/products/')[1]?.split('?')[0];
    if (!handle) return;
    fetch(`/products/${handle}.js`)
      .then(r => r.json())
      .then(product => {
        const v = product.variants.find(v => String(v.id) === String(variantId));
        if (!v) return;
        const src = v.featured_image?.src
          ? ensureSize(v.featured_image.src, 800)
          : product.images?.[0]
            ? ensureSize(product.images[0], 800)
            : null;
        if (src) updateCarpetTexture(src);
      })
      .catch(() => {});
  }

  /* ─────────────────────────────────────────────────────────────
     FIX: ensureSize — previous version caused double extensions
     like  image_800x800_800x800.jpg  on Shopify CDN URLs.
     Now strips ALL existing size suffixes first, then adds once.
  ───────────────────────────────────────────────────────────── */
  function ensureSize(url, size) {
    if (!url) return url;
    // Remove any existing Shopify size suffix like _800x800, _1024x, _x600 etc.
    return url
      .replace(/(_\d+x\d*|_\d*x\d+)(?=\.[a-z]{3,4}(\?|$))/i, '')
      .replace(/(\.[a-z]{3,4})(\?.*)?$/i, `_${size}x${size}$1$2`);
  }

  /* ── Core texture swap ────────────────────────────────────── */
  function updateCarpetTexture(imageUrl) {
    if (!imageUrl) return;
    // Allow force-update even if same URL (variant-sync may call with same)
    state.currentImage = imageUrl;
    // Also keep the global in sync so future opens use latest image
    window.__CRV_CURRENT_IMAGE__ = imageUrl;

    if (!state.initialized) return; // will be picked up on open via __CRV_CURRENT_IMAGE__

    flashCanvasUpdate();

    if (state.use3D && state.three) {
      loadCarpetTexture3D(window.THREE, state.three.carpetMaterial, imageUrl);
    } else if (state.canvas2d) {
      loadCarpetTexture2D(imageUrl).then(() => render2D());
    }
  }

  function flashCanvasUpdate() {
    if (!dom.canvasWrap) return;
    dom.canvasWrap.classList.add('texture-updating');
    setTimeout(() => dom.canvasWrap.classList.remove('texture-updating'), 600);
  }

  /* ═══════════════════════════════════════════════════════════
     UTILS
  ═══════════════════════════════════════════════════════════ */
  function showLoader(show) {
    if (!dom.loader) return;
    if (show) dom.loader.classList.remove('hidden');
    else setTimeout(() => dom.loader.classList.add('hidden'), 320);
  }

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve();
      const s = document.createElement('script');
      s.src = src;
      s.onload  = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

})();